import { api } from "@/lib/api-client";
import { cn } from "@/utils";
import {
  addToast,
  Button,
  Checkbox,
  Divider,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Skeleton,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { Fragment, useState } from "react";
import { t } from "@lingui/core/macro";
import * as Sentry from "@sentry/react";
import { groupBy } from "lodash";

export const ManageUserAppointmentAddons = ({
  userAppointments,
  isOpen,
  onClose,
  locationId,
}: {
  userAppointments: {
    userAppointmentId: number;
    serviceId: number;
    addons: {
      id: number;
      quantity: number;
    }[];
  }[];
  isOpen: boolean;
  onClose: (
    data?: Record<number, { addonId: number; quantity: number }[]>,
  ) => void;
  locationId: number;
}) => {
  const { data: serviceAddons } = api.service.useGetServiceAddons({
    disabled: !isOpen,
    query: {
      serviceIds: Array.from(
        new Set(userAppointments.map((ua) => ua.serviceId)),
      ),
      locationId,
    },
  });

  const { data: servicesData } = api.service.useGetServices({
    enabled: isOpen,
    includeDeleted: true,
  });

  const {
    mutateAsync: updateCustomerAppointment,
    isPending: isUpdatingCustomerAppointment,
  } = api.customerAppointment.useUpdateCustomerAppointment();

  const [selectedAddons, setSelectedAddons] = useState<{
    [userAppointmentId: number]: {
      addonId: number;
      quantity: number;
      serviceId: number;
    }[];
  }>(
    userAppointments.reduce(
      (acc, userAppointment) => {
        acc[userAppointment.userAppointmentId] = userAppointment.addons.map(
          (addon) => ({
            addonId: addon.id,
            quantity: addon.quantity,
            serviceId: userAppointment.serviceId,
          }),
        );
        return acc;
      },
      {} as {
        [userAppointmentId: number]: {
          addonId: number;
          quantity: number;
          serviceId: number;
        }[];
      },
    ),
  );

  const services = servicesData?.services.filter((service) =>
    userAppointments.some(
      (userAppointment) => userAppointment.serviceId === service.serviceId,
    ),
  );

  const handleSaveAddons = async () => {
    try {
      await updateCustomerAppointment({
        body: { addons: selectedAddons },
        customerAppointmentId: userAppointments[0].userAppointmentId,
      });

      addToast({
        title: t`Dodatki posodobljeni`,
        color: "success",
      });

      onClose(selectedAddons);
    } catch (e) {
      Sentry.captureException(e);

      addToast({
        title: t`Prišlo je do napake pri posodabljanju dodatkov`,
        color: "danger",
      });
    }
  };

  if (!services) {
    return <Skeleton></Skeleton>;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalContent>
        <ModalHeader>
          <Trans>Uredi dodatke</Trans>
        </ModalHeader>
        <ModalBody className="px-0">
          <div className="flex flex-col gap-2">
            {userAppointments?.map((userAppointment, index) => {
              const service = services.find(
                (s) => s.serviceId === userAppointment.serviceId,
              );

              if (!service) {
                return null;
              }

              const addons = serviceAddons?.addons.filter((addon) =>
                addon.serviceIds.includes(service.serviceId),
              );

              const addonsByCategory = groupBy(
                addons ?? [],
                (a) => a.categoryId ?? -1,
              );

              const shouldDisplayCategoryName =
                Object.entries(addonsByCategory).length > 1;

              return (
                <Fragment key={service.serviceId}>
                  <div className="flex flex-col gap-2 px-6 pb-4 pr-7">
                    {/** TODO: add startTime, need to add formatted from backend*/}
                    <p className="mb-1 font-medium">{service.name}</p>

                    <div className="flex flex-col gap-6">
                      {Object.entries(addonsByCategory).map(
                        ([categoryId, categoryAddons]) => (
                          <div key={categoryId}>
                            {shouldDisplayCategoryName && (
                              <h3 className="mb-3 text-center text-sm font-semibold text-gray-900">
                                {categoryAddons[0]?.category?.name ?? t`Drugo`}
                              </h3>
                            )}
                            <div className="flex flex-col gap-6">
                              {categoryAddons?.map((addon) => (
                                <Checkbox
                                  key={addon.id}
                                  aria-label={addon.name}
                                  classNames={{
                                    base: cn(
                                      "inline-flex w-full max-w-md bg-content1",
                                      "hover:bg-content2 items-center justify-start",
                                      "cursor-pointer rounded-lg gap-2 p-4 border-2 border-transparent",
                                      "border-default-100 data-[selected=true]:border-primary",
                                      "ml-px",
                                    ),
                                  }}
                                  className="max-w-none"
                                  isSelected={selectedAddons[
                                    userAppointment.userAppointmentId
                                  ]?.some(
                                    (selectedAddon) =>
                                      selectedAddon.addonId === addon.id,
                                  )}
                                  onValueChange={(value) => {
                                    setSelectedAddons((prev) => ({
                                      ...prev,
                                      [userAppointment.userAppointmentId]: value
                                        ? [
                                            ...(prev[
                                              userAppointment.userAppointmentId
                                            ] || []),
                                            {
                                              addonId: addon.id,
                                              quantity: 1,
                                              serviceId: service.serviceId,
                                            },
                                          ]
                                        : (
                                            prev[
                                              userAppointment.userAppointmentId
                                            ] || []
                                          ).filter(
                                            (selectedAddon) =>
                                              selectedAddon.addonId !==
                                              addon.id,
                                          ),
                                    }));
                                  }}
                                >
                                  <p>{addon.name}</p>
                                  <p>{addon.priceCentsAdjustmentFormatted}</p>
                                </Checkbox>
                              ))}
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  </div>

                  {!!serviceAddons?.addons?.length &&
                    index !== services.length - 1 && (
                      <Divider className="my-4 h-[2px] bg-gray-200" />
                    )}
                </Fragment>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={() => onClose()}>
            <Trans>Zapri</Trans>
          </Button>
          <Button
            color="primary"
            onPress={handleSaveAddons}
            isLoading={isUpdatingCustomerAppointment}
          >
            <Trans>Shrani</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
